.shell { min-height: 100vh; background: var(--page); }

.bar {
  display: flex; align-items: center; flex-wrap: wrap; gap: 16px;
  padding: 14px 24px; background: var(--invert-bg); color: var(--invert-fg);
}

/* Left group: who this is and whose language it is in. One lockup, so the
   track reads as part of the course's identity rather than as navigation. */
.identity { display: flex; align-items: baseline; gap: 10px; min-width: 0; }
.brand {
  border: none; background: none; padding: 0; cursor: pointer;
  font: 300 19px var(--serif); color: var(--invert-fg);
}
.langSep { color: var(--invert-faint); font-size: 13px; }
.lang {
  /* Subordinate to the title, not competing with it — and deliberately not the
     gold used by the crumb beside it, so the lockup has one accent, not two. */
  font: 500 13px var(--sans); color: var(--invert-dim);
  letter-spacing: .01em; white-space: nowrap;
}
/* The multi-track form. Styled as text, not as a form control: it sits in a
   title lockup, and a chrome-default select would break the line. */
.langPick {
  appearance: none; border: none; background: transparent; cursor: pointer;
  padding: 2px 16px 2px 0; border-radius: var(--radius-sm);
  background-image: linear-gradient(45deg, transparent 50%, currentColor 50%),
                    linear-gradient(135deg, currentColor 50%, transparent 50%);
  background-size: 4px 4px, 4px 4px;
  background-position: right 6px center, right 2px center;
  background-repeat: no-repeat;
}
.langPick:hover, .langPick:focus-visible { color: var(--invert-fg); }
.langPick option { color: #121212; background: #f4f4f2; }

.sep { color: var(--invert-faint); }
.crumb { font: 500 10px var(--mono); letter-spacing: .16em; text-transform: uppercase; color: var(--invert-label); }

/* Right-hand side: navigation and the theme setting, kept in one wrapping unit. */
.tools { margin-left: auto; display: flex; align-items: center; gap: 6px; }
.nav { display: flex; gap: 6px; align-items: center; }
.navLink {
  border: none; background: none; cursor: pointer; padding: 6px 10px; border-radius: var(--radius-sm);
  font: 500 12px var(--sans); color: var(--invert-dim);
  transition: color .15s ease, background-color .15s ease;
}
.navLink:hover { color: var(--invert-fg); background: color-mix(in srgb, currentColor 12%, transparent); }
.navLink[aria-current='page'] { color: var(--invert-fg); }

/* Theme: a setting, not a destination — so it is separated from the nav links
   by a rule and shows all three states at once rather than hiding two. */
.themes {
  display: flex; align-items: center; gap: 2px;
  padding-left: 14px; margin-left: 2px;
  border-left: 1px solid color-mix(in srgb, var(--invert-fg) 18%, transparent);
}
.themeBtn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; padding: 0;
  border: none; background: none; border-radius: var(--radius-sm);
  color: var(--invert-faint); cursor: pointer;
  transition: color .15s ease, background-color .15s ease;
}
.themeBtn:hover { color: var(--invert-fg); background: color-mix(in srgb, currentColor 12%, transparent); }
/* The active state is legible, not loud. A filled accent chip here would make
   the theme control the brightest thing in the bar — which is what moving it
   out of the nav was meant to stop. */
.themeOn { color: var(--invert-fg); background: color-mix(in srgb, var(--invert-fg) 16%, transparent); }
.themeOn:hover { color: var(--invert-fg); background: color-mix(in srgb, var(--invert-fg) 22%, transparent); }

/* 1160 is the artboard's card width, not a design rule — the same trap as
   --page. The reader needs 236 rail + 112 padding + 62ch prose + 32 gap + 210
   margin column = 1197 before it can show prose at its designed measure, so a
   1160 shell silently squeezed body text narrower than the lede above it.
   Screens opt wider or narrower from here; nothing is capped arbitrarily. */
.main { width: 100%; max-width: 1400px; margin: 0 auto; background: var(--paper); }
.main.wide { max-width: 1560px; }
/* The reader draws full-bleed bands, so it takes the whole width and each act
   constrains its own content. */
.main.full { max-width: none; }

.missing { padding: 80px 48px; text-align: center; }
.missing h1 { font: 300 34px var(--serif); color: var(--ink); margin: 0; }
.missing p { font: 400 14px var(--sans); color: var(--ink-68); margin-top: 12px; }
.missing code { font: 500 13px var(--mono); }

@media (max-width: 620px) {
  .bar { padding: 12px 16px; gap: 8px 10px; }
  .crumb, .sep { display: none; }
  .brand { font-size: 17px; }
  .identity { flex: 1 1 100%; }      /* title owns row one */
  .tools { margin-left: 0; width: 100%; }
  .nav { margin-right: auto; }        /* links left, icons right, one row */
  .themes { padding-left: 10px; }
  .navLink { padding: 6px 8px; }
}

/* Language picker — a course-long choice, set once. Deliberately quiet: it is
   not a thing to fiddle with, and it is never offered per block. */

